import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useCaixas } from "@/hooks/useCaixas";
import { useAuth } from "@/contexts/AuthContext";
import { LoadingScreen } from "@/components/layout/LoadingScreen";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Plus, Lock, ChevronDown, ChevronUp, Calendar, ChevronRight, Filter } from "lucide-react";
import { CloseCaixaModal } from "./CloseCaixaModal";
import { CashSessionDetailsModal } from "./CashSessionDetailsModal";

function DailySummaryPanel({ date, authAxios }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [dayRegister, setDayRegister] = useState(undefined); // undefined=loading, null=none

  // Independent query for the month of the selected date — decoupled from the
  // main month filter so the badge is always accurate regardless of what month
  // the user has selected in the list above.
  useEffect(() => {
    if (!date) return;
    setDayRegister(undefined);
    const month = date.slice(0, 7);
    authAxios.get(`/financiero/cash-registers?month=${month}`)
      .then(r => {
        const found = (r.data || []).find(
          reg => reg.closed_at?.slice(0, 10) === date || (!reg.closed_at && reg.opened_at?.slice(0, 10) === date)
        );
        setDayRegister(found || null);
      })
      .catch(() => setDayRegister(null));
  }, [date, authAxios]);

  useEffect(() => {
    if (!expanded || !date) return;
    setLoading(true);
    authAxios.get(`/pos/caixa/resumo?date=${date}`)
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [expanded, date, authAxios]);

  const fmt = (v) => `€${(v || 0).toFixed(2)}`;

  const hasFormalCaixa = !!dayRegister;

  return (
    <Card className={`border-2 ${hasFormalCaixa ? "border-emerald-200 bg-emerald-50/30" : "border-blue-200 bg-blue-50/30"}`}>
      <CardContent className="p-4">
        <button
          className="w-full flex items-center justify-between text-left"
          onClick={() => setExpanded(e => !e)}
        >
          <div className="flex items-center gap-2 flex-wrap">
            <Calendar className={`w-4 h-4 ${hasFormalCaixa ? "text-emerald-600" : "text-blue-600"}`} />
            <span className={`font-semibold text-sm ${hasFormalCaixa ? "text-emerald-800" : "text-blue-800"}`}>Resumo do dia: {date}</span>
            {dayRegister === undefined ? null : hasFormalCaixa ? (
              <Badge className="bg-emerald-100 text-emerald-700 text-xs">
                caixa #{dayRegister.id?.slice(-6).toUpperCase()} — {dayRegister.status === "open" ? "aberto" : "fechado"}
              </Badge>
            ) : (
              <Badge className="bg-amber-100 text-amber-700 text-xs">sem caixa formal</Badge>
            )}
          </div>
          {expanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
        </button>

        {expanded && (
          <div className="mt-3">
            {loading && <p className="text-xs text-gray-400">A carregar...</p>}
            {data && (
              <div className="space-y-2 text-sm">
                <div className="flex justify-between font-bold text-emerald-700 border-b pb-1">
                  <span>Total vendas</span>
                  <span>{fmt(data.total_vendas)}</span>
                </div>
                {Object.entries(data.metodos || {}).map(([m, v]) => (
                  <div key={m} className="flex justify-between text-gray-600">
                    <span>{m}</span><span>{fmt(v)}</span>
                  </div>
                ))}
                {(data.cancelamentos || 0) > 0 && (
                  <div className="flex justify-between text-red-500 text-xs border-t pt-1">
                    <span>Cancelamentos</span><span>{fmt(data.cancelamentos)}</span>
                  </div>
                )}
                <p className="text-xs text-gray-400 mt-1">{(data.vendas || []).length} transações registadas</p>
              </div>
            )}
            {!loading && !data && (
              <p className="text-xs text-gray-400 mt-2">Sem dados para esta data.</p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export const CaixasTab = () => {
  const { t } = useTranslation();
  const { authAxios } = useAuth();
  const {
    registers, loading,
    month, setMonth,
    showOpen, setShowOpen,
    openingAmount, setOpeningAmount,
    showClose, setShowClose,
    showDetail, setShowDetail,
    activeReg,
    openRegister, fetchRegisters,
  } = useCaixas();

  const today = new Date().toISOString().slice(0, 10);
  const yesterday = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
  const [historyDate, setHistoryDate] = useState(yesterday);

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-muted-foreground">Registro de apertura y cierre de caja</p>
        {!activeReg ? (
          <Button className="gap-2 glow-orange" onClick={() => setShowOpen(true)} data-testid="open-caja-btn">
            <Plus className="w-4 h-4" />Abrir Caja
          </Button>
        ) : (
          <Button variant="destructive" className="gap-2" onClick={() => setShowClose(activeReg)} data-testid="close-caja-btn">
            <Lock className="w-4 h-4" />Cerrar Caja
          </Button>
        )}
      </div>

      {activeReg && (
        <Card className="border-green-500/30 bg-green-500/5">
          <CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-3 h-3 rounded-full bg-green-500 animate-pulse" />
              <div>
                <p className="font-semibold text-green-700">Caja Abierta</p>
                <p className="text-xs text-muted-foreground">
                  Apertura: {new Date(activeReg.opened_at).toLocaleString("es-ES")} | Monto inicial: {activeReg.opening_amount.toFixed(2)}€ | Por: {activeReg.opened_by}
                </p>
              </div>
            </div>
            <Badge className="bg-green-500/20 text-green-600">Activa</Badge>
          </CardContent>
        </Card>
      )}

      <div className="flex items-center gap-2 rounded-xl border px-4 py-3 bg-gray-50">
        <Filter className="w-4 h-4 text-gray-400 shrink-0" />
        <span className="text-xs text-gray-500 shrink-0">Mês:</span>
        <Input
          type="month"
          value={month}
          onChange={e => setMonth(e.target.value)}
          className="w-36 h-8 text-sm"
        />
        <span className="text-xs text-gray-400">
          {registers.filter(r => r.status === "closed").length} fechado{registers.filter(r => r.status === "closed").length !== 1 ? "s" : ""}
          {registers.some(r => r.status === "open") ? " · 1 aberto" : ""}
        </span>
      </div>

      <div className="rounded-xl border p-4 space-y-3 bg-gray-50">
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-gray-500" />
          <span className="text-sm font-semibold text-gray-700">Ver resumo de outro dia</span>
        </div>
        <div className="flex gap-2">
          <Input
            type="date"
            value={historyDate}
            max={today}
            onChange={e => setHistoryDate(e.target.value)}
            className="w-full sm:w-44"
          />
        </div>
        {historyDate && <DailySummaryPanel date={historyDate} authAxios={authAxios} />}
      </div>

      {loading ? <LoadingScreen /> : registers.map(r => (
        <Card
          key={r.id}
          className="card-hover cursor-pointer hover:border-orange-300 hover:shadow-sm transition-all"
          onClick={() => setShowDetail(r)}
          role="button"
          aria-label={`Ver detalhes do caixa ${r.id.slice(-6).toUpperCase()}`}
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between gap-3">
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm">Caixa #{r.id.slice(-6).toUpperCase()}</p>
                <p className="text-xs text-muted-foreground">
                  Abertura: {new Date(r.opened_at).toLocaleString("pt-PT")} | Inicial: {r.opening_amount.toFixed(2)}€
                </p>
                {r.closed_at && (
                  <p className="text-xs text-muted-foreground">
                    Fecho: {new Date(r.closed_at).toLocaleString("pt-PT")} | Final: {(r.closing_amount || 0).toFixed(2)}€ | Por: {r.closed_by || "—"}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {r.status === "open"
                  ? <Badge className="bg-green-500/20 text-green-700">Aberto</Badge>
                  : <Badge className="bg-gray-500/20 text-gray-400">Fechado</Badge>}
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
      {registers.filter(r => r.status === "closed").length === 0 && !loading && (
        <Card className="border-dashed border-gray-200">
          <CardContent className="py-10 text-center space-y-1">
            <p className="text-sm text-muted-foreground">
              Nenhum caixa fechado em {(() => {
                const [y, m] = month.split("-");
                const n = ["","jan","fev","mar","abr","mai","jun","jul","ago","set","out","nov","dez"];
                return `${n[parseInt(m)] || m}/${y}`;
              })()}.
            </p>
            <p className="text-xs text-gray-400">Use o selector de mês acima para ver o histórico.</p>
          </CardContent>
        </Card>
      )}

      <Dialog open={showOpen} onOpenChange={setShowOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>{t("financiero.openCash")}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">Informe el valor inicial con el que se abrirá la caja.</p>
            <div>
              <Label className="text-xs">Valor inicial</Label>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-lg font-bold text-muted-foreground">€</span>
                <Input type="number" step="0.01" min="0" value={openingAmount} onChange={e => setOpeningAmount(e.target.value)} placeholder="0.00" className="text-lg font-semibold" data-testid="opening-amount-input" autoFocus />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOpen(false)}>{t("common.cancel")}</Button>
            <Button onClick={openRegister} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="confirm-open-caja">{t("financiero.openCash")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CloseCaixaModal
        open={!!showClose}
        onClose={() => setShowClose(null)}
        authAxios={authAxios}
        onSuccess={fetchRegisters}
      />

      <CashSessionDetailsModal
        open={!!showDetail}
        onClose={() => setShowDetail(null)}
        register={showDetail}
        authAxios={authAxios}
      />
    </div>
  );
};
